import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Shield, Users, Database, Clock, Share2, UserCheck, Mail } from 'lucide-react';
import { FlagImg } from '../components/FlagImg';

export const PrivacyPolicy = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Privacy Policy',
      subtitle: 'How we collect, use, and protect your personal data',
      lastUpdated: 'Last updated: February 2025',
      intro: {
        title: 'Introduction',
        text: 'TRH Steel is committed to protecting your privacy and ensuring the security of your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or engage with our services. This policy complies with the General Data Protection Regulation (GDPR) and other applicable data protection laws.'
      },
      controllers: {
        title: 'Data Controllers',
        text: 'Depending on your location and the nature of your interaction with TRH Steel, the following entities may act as data controllers:',
        spain: {
          name: 'TRH ZF SEVILLA SOCIEDAD LIMITADA',
          id: 'CIF: B90390345',
          address: 'Carretera de la Esclusa, s/n, Zona Franca Puerto de Sevilla, 41011 Sevilla, Spain'
        },
        belgium: {
          name: 'TRH Liège SRL',
          id: 'VAT: BE 0545.820.384',
          address: 'Rue de l\'Ile Monsin 107, BE-4020 Liège, Belgium'
        }
      },
      dataCollected: {
        title: 'Personal Data We Collect',
        categories: [
          { type: 'Identity Data', examples: 'Name, job title, company name' },
          { type: 'Contact Data', examples: 'Email address, phone number, postal address' },
          { type: 'Transaction Data', examples: 'Details of orders, quotes, and commercial interactions' },
          { type: 'Technical Data', examples: 'IP address, browser type, device information, cookies' },
          { type: 'Communication Data', examples: 'Content of messages sent through contact forms or email' }
        ]
      },
      purposes: {
        title: 'Purposes of Data Processing',
        items: [
          { purpose: 'Contract Performance', desc: 'Processing orders, managing customer accounts, delivering products and services' },
          { purpose: 'Communication', desc: 'Responding to inquiries, providing customer support, sending service-related notifications' },
          { purpose: 'Legal Compliance', desc: 'Meeting legal obligations, tax requirements, and regulatory compliance' },
          { purpose: 'Business Operations', desc: 'Internal analysis, improving our services, quality assurance' },
          { purpose: 'Marketing', desc: 'Sending commercial communications (only with explicit consent)' }
        ]
      },
      legalBasis: {
        title: 'Legal Basis for Processing',
        items: [
          { basis: 'Contractual Necessity', desc: 'Processing necessary for the performance of a contract or pre-contractual measures' },
          { basis: 'Legal Obligation', desc: 'Processing required to comply with legal requirements' },
          { basis: 'Legitimate Interest', desc: 'Processing necessary for our legitimate business interests, provided they do not override your rights' },
          { basis: 'Consent', desc: 'Processing based on your explicit consent, which can be withdrawn at any time' }
        ]
      },
      retention: {
        title: 'Data Retention',
        text: 'We retain personal data only for as long as necessary to fulfill the purposes for which it was collected:',
        periods: [
          { data: 'Customer account data', period: 'Duration of business relationship + 5 years' },
          { data: 'Transaction records', period: '10 years (legal requirement)' },
          { data: 'Marketing consent records', period: 'Until consent is withdrawn + 3 years' },
          { data: 'Website analytics', period: '26 months' }
        ]
      },
      sharing: {
        title: 'Data Sharing',
        text: 'We may share your personal data with:',
        recipients: [
          'Service providers (IT services, logistics, payment processing)',
          'Professional advisors (legal, accounting)',
          'Regulatory authorities when required by law',
          'Other TRH Steel entities for internal administrative purposes'
        ],
        safeguards: 'When transferring data outside the European Economic Area, we ensure appropriate safeguards are in place, including Standard Contractual Clauses approved by the European Commission.'
      },
      rights: {
        title: 'Your Rights Under GDPR',
        items: [
          { right: 'Access', desc: 'Request a copy of your personal data' },
          { right: 'Rectification', desc: 'Request correction of inaccurate data' },
          { right: 'Erasure', desc: 'Request deletion of your data ("right to be forgotten")' },
          { right: 'Restriction', desc: 'Request limitation of processing' },
          { right: 'Portability', desc: 'Receive your data in a structured, machine-readable format' },
          { right: 'Objection', desc: 'Object to processing based on legitimate interests or direct marketing' },
          { right: 'Withdraw Consent', desc: 'Withdraw consent at any time without affecting prior processing' }
        ]
      },
      contact: {
        title: 'Contact for Data Protection',
        text: 'To exercise your rights or for any questions regarding this Privacy Policy, please contact us:',
        email: 'privacy@trhsteel.com',
        address: 'Data Protection Officer, TRH Steel, info@trhsteel.com'
      },
      complaints: {
        title: 'Right to Lodge a Complaint',
        text: 'You have the right to lodge a complaint with a supervisory authority if you believe your data protection rights have been violated. In Spain, contact the Agencia Española de Protección de Datos (AEPD). In Belgium, contact the Autorité de protection des données (APD).'
      }
    },
    es: {
      title: 'Política de Privacidad',
      subtitle: 'Cómo recopilamos, usamos y protegemos sus datos personales',
      lastUpdated: 'Última actualización: Febrero 2025',
      intro: {
        title: 'Introducción',
        text: 'TRH Steel se compromete a proteger su privacidad y garantizar la seguridad de sus datos personales. Esta Política de Privacidad explica cómo recopilamos, utilizamos, divulgamos y protegemos su información cuando visita nuestro sitio web o interactúa con nuestros servicios. Esta política cumple con el Reglamento General de Protección de Datos (RGPD) y otras leyes de protección de datos aplicables.'
      },
      controllers: {
        title: 'Responsables del Tratamiento',
        text: 'Dependiendo de su ubicación y la naturaleza de su interacción con TRH Steel, las siguientes entidades pueden actuar como responsables del tratamiento:',
        spain: {
          name: 'TRH ZF SEVILLA SOCIEDAD LIMITADA',
          id: 'CIF: B90390345',
          address: 'Carretera de la Esclusa, s/n, Zona Franca Puerto de Sevilla, 41011 Sevilla, España'
        },
        belgium: {
          name: 'TRH Liège SRL',
          id: 'IVA: BE 0545.820.384',
          address: 'Rue de l\'Ile Monsin 107, BE-4020 Lieja, Bélgica'
        }
      },
      dataCollected: {
        title: 'Datos Personales que Recopilamos',
        categories: [
          { type: 'Datos de Identidad', examples: 'Nombre, cargo, nombre de empresa' },
          { type: 'Datos de Contacto', examples: 'Correo electrónico, número de teléfono, dirección postal' },
          { type: 'Datos de Transacción', examples: 'Detalles de pedidos, presupuestos e interacciones comerciales' },
          { type: 'Datos Técnicos', examples: 'Dirección IP, tipo de navegador, información del dispositivo, cookies' },
          { type: 'Datos de Comunicación', examples: 'Contenido de mensajes enviados a través de formularios de contacto o email' }
        ]
      },
      purposes: {
        title: 'Finalidades del Tratamiento',
        items: [
          { purpose: 'Ejecución Contractual', desc: 'Procesamiento de pedidos, gestión de cuentas de clientes, entrega de productos y servicios' },
          { purpose: 'Comunicación', desc: 'Respuesta a consultas, soporte al cliente, envío de notificaciones relacionadas con el servicio' },
          { purpose: 'Cumplimiento Legal', desc: 'Cumplimiento de obligaciones legales, requisitos fiscales y normativas' },
          { purpose: 'Operaciones Comerciales', desc: 'Análisis interno, mejora de nuestros servicios, control de calidad' },
          { purpose: 'Marketing', desc: 'Envío de comunicaciones comerciales (solo con consentimiento explícito)' }
        ]
      },
      legalBasis: {
        title: 'Base Legal del Tratamiento',
        items: [
          { basis: 'Necesidad Contractual', desc: 'Tratamiento necesario para la ejecución de un contrato o medidas precontractuales' },
          { basis: 'Obligación Legal', desc: 'Tratamiento requerido para cumplir con requisitos legales' },
          { basis: 'Interés Legítimo', desc: 'Tratamiento necesario para nuestros intereses comerciales legítimos, siempre que no prevalezcan sus derechos' },
          { basis: 'Consentimiento', desc: 'Tratamiento basado en su consentimiento explícito, que puede retirarse en cualquier momento' }
        ]
      },
      retention: {
        title: 'Conservación de Datos',
        text: 'Conservamos los datos personales solo durante el tiempo necesario para cumplir los fines para los que fueron recopilados:',
        periods: [
          { data: 'Datos de cuenta de cliente', period: 'Duración de la relación comercial + 5 años' },
          { data: 'Registros de transacciones', period: '10 años (requisito legal)' },
          { data: 'Registros de consentimiento de marketing', period: 'Hasta la retirada del consentimiento + 3 años' },
          { data: 'Analítica web', period: '26 meses' }
        ]
      },
      sharing: {
        title: 'Compartición de Datos',
        text: 'Podemos compartir sus datos personales con:',
        recipients: [
          'Proveedores de servicios (servicios IT, logística, procesamiento de pagos)',
          'Asesores profesionales (legales, contables)',
          'Autoridades reguladoras cuando lo exija la ley',
          'Otras entidades de TRH Steel para fines administrativos internos'
        ],
        safeguards: 'Al transferir datos fuera del Espacio Económico Europeo, garantizamos que se aplican las salvaguardas adecuadas, incluyendo las Cláusulas Contractuales Tipo aprobadas por la Comisión Europea.'
      },
      rights: {
        title: 'Sus Derechos bajo el RGPD',
        items: [
          { right: 'Acceso', desc: 'Solicitar una copia de sus datos personales' },
          { right: 'Rectificación', desc: 'Solicitar la corrección de datos inexactos' },
          { right: 'Supresión', desc: 'Solicitar la eliminación de sus datos ("derecho al olvido")' },
          { right: 'Limitación', desc: 'Solicitar la limitación del tratamiento' },
          { right: 'Portabilidad', desc: 'Recibir sus datos en un formato estructurado y legible por máquina' },
          { right: 'Oposición', desc: 'Oponerse al tratamiento basado en intereses legítimos o marketing directo' },
          { right: 'Retirar Consentimiento', desc: 'Retirar el consentimiento en cualquier momento sin afectar el tratamiento previo' }
        ]
      },
      contact: {
        title: 'Contacto para Protección de Datos',
        text: 'Para ejercer sus derechos o para cualquier consulta sobre esta Política de Privacidad, contáctenos:',
        email: 'privacy@trhsteel.com',
        address: 'Delegado de Protección de Datos, TRH Steel, info@trhsteel.com'
      },
      complaints: {
        title: 'Derecho a Presentar una Reclamación',
        text: 'Tiene derecho a presentar una reclamación ante una autoridad de control si considera que se han vulnerado sus derechos de protección de datos. En España, contacte con la Agencia Española de Protección de Datos (AEPD). En Bélgica, contacte con la Autorité de protection des données (APD).'
      }
    },
    fr: {
      title: 'Politique de Confidentialité',
      subtitle: 'Comment nous collectons, utilisons et protégeons vos données personnelles',
      lastUpdated: 'Dernière mise à jour: Février 2025',
      intro: {
        title: 'Introduction',
        text: 'TRH Steel s\'engage à protéger votre vie privée et à assurer la sécurité de vos données personnelles. Cette Politique de Confidentialité explique comment nous collectons, utilisons, divulguons et protégeons vos informations lorsque vous visitez notre site web ou utilisez nos services. Cette politique est conforme au Règlement Général sur la Protection des Données (RGPD) et aux autres lois applicables en matière de protection des données.'
      },
      controllers: {
        title: 'Responsables du Traitement',
        text: 'Selon votre localisation et la nature de votre interaction avec TRH Steel, les entités suivantes peuvent agir en tant que responsables du traitement:',
        spain: {
          name: 'TRH ZF SEVILLA SOCIEDAD LIMITADA',
          id: 'CIF: B90390345',
          address: 'Carretera de la Esclusa, s/n, Zona Franca Puerto de Sevilla, 41011 Séville, Espagne'
        },
        belgium: {
          name: 'TRH Liège SRL',
          id: 'TVA: BE 0545.820.384',
          address: 'Rue de l\'Ile Monsin 107, BE-4020 Liège, Belgique'
        }
      },
      dataCollected: {
        title: 'Données Personnelles Collectées',
        categories: [
          { type: 'Données d\'Identité', examples: 'Nom, fonction, nom de l\'entreprise' },
          { type: 'Données de Contact', examples: 'Adresse email, numéro de téléphone, adresse postale' },
          { type: 'Données de Transaction', examples: 'Détails des commandes, devis et interactions commerciales' },
          { type: 'Données Techniques', examples: 'Adresse IP, type de navigateur, informations sur l\'appareil, cookies' },
          { type: 'Données de Communication', examples: 'Contenu des messages envoyés via les formulaires de contact ou email' }
        ]
      },
      purposes: {
        title: 'Finalités du Traitement',
        items: [
          { purpose: 'Exécution Contractuelle', desc: 'Traitement des commandes, gestion des comptes clients, livraison des produits et services' },
          { purpose: 'Communication', desc: 'Réponse aux demandes, support client, envoi de notifications liées au service' },
          { purpose: 'Conformité Légale', desc: 'Respect des obligations légales, exigences fiscales et conformité réglementaire' },
          { purpose: 'Opérations Commerciales', desc: 'Analyse interne, amélioration de nos services, assurance qualité' },
          { purpose: 'Marketing', desc: 'Envoi de communications commerciales (uniquement avec consentement explicite)' }
        ]
      },
      legalBasis: {
        title: 'Base Juridique du Traitement',
        items: [
          { basis: 'Nécessité Contractuelle', desc: 'Traitement nécessaire à l\'exécution d\'un contrat ou de mesures précontractuelles' },
          { basis: 'Obligation Légale', desc: 'Traitement requis pour se conformer aux exigences légales' },
          { basis: 'Intérêt Légitime', desc: 'Traitement nécessaire à nos intérêts commerciaux légitimes, à condition qu\'ils ne prévalent pas sur vos droits' },
          { basis: 'Consentement', desc: 'Traitement basé sur votre consentement explicite, qui peut être retiré à tout moment' }
        ]
      },
      retention: {
        title: 'Conservation des Données',
        text: 'Nous conservons les données personnelles uniquement pendant la durée nécessaire aux finalités pour lesquelles elles ont été collectées:',
        periods: [
          { data: 'Données de compte client', period: 'Durée de la relation commerciale + 5 ans' },
          { data: 'Registres de transactions', period: '10 ans (exigence légale)' },
          { data: 'Registres de consentement marketing', period: 'Jusqu\'au retrait du consentement + 3 ans' },
          { data: 'Analytique web', period: '26 mois' }
        ]
      },
      sharing: {
        title: 'Partage des Données',
        text: 'Nous pouvons partager vos données personnelles avec:',
        recipients: [
          'Prestataires de services (services informatiques, logistique, traitement des paiements)',
          'Conseillers professionnels (juridiques, comptables)',
          'Autorités réglementaires lorsque la loi l\'exige',
          'Autres entités TRH Steel à des fins administratives internes'
        ],
        safeguards: 'Lors du transfert de données en dehors de l\'Espace Économique Européen, nous garantissons que des garanties appropriées sont en place, y compris les Clauses Contractuelles Types approuvées par la Commission Européenne.'
      },
      rights: {
        title: 'Vos Droits en vertu du RGPD',
        items: [
          { right: 'Accès', desc: 'Demander une copie de vos données personnelles' },
          { right: 'Rectification', desc: 'Demander la correction de données inexactes' },
          { right: 'Effacement', desc: 'Demander la suppression de vos données ("droit à l\'oubli")' },
          { right: 'Limitation', desc: 'Demander la limitation du traitement' },
          { right: 'Portabilité', desc: 'Recevoir vos données dans un format structuré et lisible par machine' },
          { right: 'Opposition', desc: 'S\'opposer au traitement basé sur des intérêts légitimes ou le marketing direct' },
          { right: 'Retrait du Consentement', desc: 'Retirer le consentement à tout moment sans affecter le traitement antérieur' }
        ]
      },
      contact: {
        title: 'Contact pour la Protection des Données',
        text: 'Pour exercer vos droits ou pour toute question concernant cette Politique de Confidentialité, veuillez nous contacter:',
        email: 'privacy@trhsteel.com',
        address: 'Délégué à la Protection des Données, TRH Steel, info@trhsteel.com'
      },
      complaints: {
        title: 'Droit de Déposer une Plainte',
        text: 'Vous avez le droit de déposer une plainte auprès d\'une autorité de contrôle si vous estimez que vos droits en matière de protection des données ont été violés. En Espagne, contactez l\'Agencia Española de Protección de Datos (AEPD). En Belgique, contactez l\'Autorité de protection des données (APD).'
      }
    },
    de: {
      title: 'Datenschutzerklärung',
      subtitle: 'Wie wir Ihre personenbezogenen Daten erheben, verwenden und schützen',
      lastUpdated: 'Letzte Aktualisierung: Februar 2025',
      intro: {
        title: 'Einführung',
        text: 'TRH Steel verpflichtet sich zum Schutz Ihrer Privatsphäre und zur Gewährleistung der Sicherheit Ihrer personenbezogenen Daten. Diese Datenschutzerklärung erläutert, wie wir Ihre Daten erheben, verwenden, offenlegen und schützen, wenn Sie unsere Website besuchen oder unsere Dienste nutzen. Diese Richtlinie entspricht der Datenschutz-Grundverordnung (DSGVO) und anderen geltenden Datenschutzgesetzen.'
      },
      controllers: {
        title: 'Verantwortliche',
        text: 'Je nach Ihrem Standort und der Art Ihrer Interaktion mit TRH Steel können folgende Unternehmen als Verantwortliche fungieren:',
        spain: {
          name: 'TRH ZF SEVILLA SOCIEDAD LIMITADA',
          id: 'CIF: B90390345',
          address: 'Carretera de la Esclusa, s/n, Zona Franca Puerto de Sevilla, 41011 Sevilla, Spanien'
        },
        belgium: {
          name: 'TRH Liège SRL',
          id: 'USt-IdNr.: BE 0545.820.384',
          address: 'Rue de l\'Ile Monsin 107, BE-4020 Lüttich, Belgien'
        }
      },
      dataCollected: {
        title: 'Erhobene Personenbezogene Daten',
        categories: [
          { type: 'Identitätsdaten', examples: 'Name, Position, Firmenname' },
          { type: 'Kontaktdaten', examples: 'E-Mail-Adresse, Telefonnummer, Postanschrift' },
          { type: 'Transaktionsdaten', examples: 'Details zu Bestellungen, Angeboten und geschäftlichen Interaktionen' },
          { type: 'Technische Daten', examples: 'IP-Adresse, Browsertyp, Geräteinformationen, Cookies' },
          { type: 'Kommunikationsdaten', examples: 'Inhalt von Nachrichten über Kontaktformulare oder E-Mail' }
        ]
      },
      purposes: {
        title: 'Zwecke der Verarbeitung',
        items: [
          { purpose: 'Vertragserfüllung', desc: 'Bearbeitung von Bestellungen, Verwaltung von Kundenkonten, Lieferung von Produkten und Dienstleistungen' },
          { purpose: 'Kommunikation', desc: 'Beantwortung von Anfragen, Kundenservice, Versand servicebezogener Mitteilungen' },
          { purpose: 'Rechtliche Compliance', desc: 'Erfüllung gesetzlicher Verpflichtungen, steuerlicher Anforderungen und regulatorischer Compliance' },
          { purpose: 'Geschäftsbetrieb', desc: 'Interne Analyse, Verbesserung unserer Dienste, Qualitätssicherung' },
          { purpose: 'Marketing', desc: 'Versand kommerzieller Mitteilungen (nur mit ausdrücklicher Einwilligung)' }
        ]
      },
      legalBasis: {
        title: 'Rechtsgrundlage der Verarbeitung',
        items: [
          { basis: 'Vertragliche Notwendigkeit', desc: 'Verarbeitung zur Erfüllung eines Vertrags oder vorvertraglicher Maßnahmen erforderlich' },
          { basis: 'Rechtliche Verpflichtung', desc: 'Verarbeitung zur Einhaltung gesetzlicher Anforderungen erforderlich' },
          { basis: 'Berechtigtes Interesse', desc: 'Verarbeitung für unsere berechtigten Geschäftsinteressen erforderlich, sofern Ihre Rechte nicht überwiegen' },
          { basis: 'Einwilligung', desc: 'Verarbeitung auf Grundlage Ihrer ausdrücklichen Einwilligung, die jederzeit widerrufen werden kann' }
        ]
      },
      retention: {
        title: 'Datenspeicherung',
        text: 'Wir speichern personenbezogene Daten nur so lange, wie es für die Zwecke erforderlich ist, für die sie erhoben wurden:',
        periods: [
          { data: 'Kundenkontodaten', period: 'Dauer der Geschäftsbeziehung + 5 Jahre' },
          { data: 'Transaktionsaufzeichnungen', period: '10 Jahre (gesetzliche Anforderung)' },
          { data: 'Marketing-Einwilligungsaufzeichnungen', period: 'Bis zum Widerruf der Einwilligung + 3 Jahre' },
          { data: 'Website-Analytik', period: '26 Monate' }
        ]
      },
      sharing: {
        title: 'Datenweitergabe',
        text: 'Wir können Ihre personenbezogenen Daten weitergeben an:',
        recipients: [
          'Dienstleister (IT-Dienste, Logistik, Zahlungsabwicklung)',
          'Professionelle Berater (Rechts-, Buchhaltungsberater)',
          'Aufsichtsbehörden, wenn gesetzlich vorgeschrieben',
          'Andere TRH Steel-Unternehmen für interne Verwaltungszwecke'
        ],
        safeguards: 'Bei der Übermittlung von Daten außerhalb des Europäischen Wirtschaftsraums stellen wir sicher, dass angemessene Garantien vorhanden sind, einschließlich der von der Europäischen Kommission genehmigten Standardvertragsklauseln.'
      },
      rights: {
        title: 'Ihre Rechte nach der DSGVO',
        items: [
          { right: 'Auskunft', desc: 'Eine Kopie Ihrer personenbezogenen Daten anfordern' },
          { right: 'Berichtigung', desc: 'Korrektur unrichtiger Daten verlangen' },
          { right: 'Löschung', desc: 'Löschung Ihrer Daten verlangen ("Recht auf Vergessenwerden")' },
          { right: 'Einschränkung', desc: 'Einschränkung der Verarbeitung verlangen' },
          { right: 'Datenübertragbarkeit', desc: 'Ihre Daten in einem strukturierten, maschinenlesbaren Format erhalten' },
          { right: 'Widerspruch', desc: 'Der Verarbeitung aufgrund berechtigter Interessen oder Direktmarketing widersprechen' },
          { right: 'Widerruf der Einwilligung', desc: 'Einwilligung jederzeit widerrufen, ohne die vorherige Verarbeitung zu beeinflussen' }
        ]
      },
      contact: {
        title: 'Kontakt für Datenschutz',
        text: 'Um Ihre Rechte auszuüben oder bei Fragen zu dieser Datenschutzerklärung kontaktieren Sie uns bitte:',
        email: 'privacy@trhsteel.com',
        address: 'Datenschutzbeauftragter, TRH Steel, info@trhsteel.com'
      },
      complaints: {
        title: 'Beschwerderecht',
        text: 'Sie haben das Recht, eine Beschwerde bei einer Aufsichtsbehörde einzureichen, wenn Sie der Meinung sind, dass Ihre Datenschutzrechte verletzt wurden. In Spanien wenden Sie sich an die Agencia Española de Protección de Datos (AEPD). In Belgien wenden Sie sich an die Autorité de protection des données (APD).'
      }
    },
    nl: {
      title: 'Privacybeleid',
      subtitle: 'Hoe wij uw persoonsgegevens verzamelen, gebruiken en beschermen',
      lastUpdated: 'Laatst bijgewerkt: Februari 2025',
      intro: {
        title: 'Inleiding',
        text: 'TRH Steel zet zich in voor de bescherming van uw privacy en de beveiliging van uw persoonsgegevens. Dit Privacybeleid legt uit hoe wij uw gegevens verzamelen, gebruiken, openbaar maken en beschermen wanneer u onze website bezoekt of gebruik maakt van onze diensten. Dit beleid is in overeenstemming met de Algemene Verordening Gegevensbescherming (AVG) en andere toepasselijke wetgeving inzake gegevensbescherming.'
      },
      controllers: {
        title: 'Verwerkingsverantwoordelijken',
        text: 'Afhankelijk van uw locatie en de aard van uw interactie met TRH Steel, kunnen de volgende entiteiten optreden als verwerkingsverantwoordelijken:',
        spain: {
          name: 'TRH ZF SEVILLA SOCIEDAD LIMITADA',
          id: 'CIF: B90390345',
          address: 'Carretera de la Esclusa, s/n, Zona Franca Puerto de Sevilla, 41011 Sevilla, Spanje'
        },
        belgium: {
          name: 'TRH Liège SRL',
          id: 'BTW: BE 0545.820.384',
          address: 'Rue de l\'Ile Monsin 107, BE-4020 Luik, België'
        }
      },
      dataCollected: {
        title: 'Verzamelde Persoonsgegevens',
        categories: [
          { type: 'Identiteitsgegevens', examples: 'Naam, functie, bedrijfsnaam' },
          { type: 'Contactgegevens', examples: 'E-mailadres, telefoonnummer, postadres' },
          { type: 'Transactiegegevens', examples: 'Details van bestellingen, offertes en commerciële interacties' },
          { type: 'Technische Gegevens', examples: 'IP-adres, browsertype, apparaatinformatie, cookies' },
          { type: 'Communicatiegegevens', examples: 'Inhoud van berichten verzonden via contactformulieren of e-mail' }
        ]
      },
      purposes: {
        title: 'Doeleinden van Verwerking',
        items: [
          { purpose: 'Uitvoering Overeenkomst', desc: 'Verwerking van bestellingen, beheer van klantenaccounts, levering van producten en diensten' },
          { purpose: 'Communicatie', desc: 'Beantwoording van vragen, klantenondersteuning, verzending van servicegerelateerde meldingen' },
          { purpose: 'Wettelijke Naleving', desc: 'Voldoen aan wettelijke verplichtingen, fiscale vereisten en regelgevende naleving' },
          { purpose: 'Bedrijfsactiviteiten', desc: 'Interne analyse, verbetering van onze diensten, kwaliteitsborging' },
          { purpose: 'Marketing', desc: 'Verzending van commerciële communicatie (alleen met uitdrukkelijke toestemming)' }
        ]
      },
      legalBasis: {
        title: 'Rechtsgrondslag voor Verwerking',
        items: [
          { basis: 'Contractuele Noodzaak', desc: 'Verwerking noodzakelijk voor de uitvoering van een overeenkomst of precontractuele maatregelen' },
          { basis: 'Wettelijke Verplichting', desc: 'Verwerking vereist om te voldoen aan wettelijke vereisten' },
          { basis: 'Gerechtvaardigd Belang', desc: 'Verwerking noodzakelijk voor onze gerechtvaardigde bedrijfsbelangen, mits uw rechten niet prevaleren' },
          { basis: 'Toestemming', desc: 'Verwerking op basis van uw uitdrukkelijke toestemming, die op elk moment kan worden ingetrokken' }
        ]
      },
      retention: {
        title: 'Gegevensbewaring',
        text: 'Wij bewaren persoonsgegevens alleen zolang als nodig is voor de doeleinden waarvoor ze zijn verzameld:',
        periods: [
          { data: 'Klantaccountgegevens', period: 'Duur zakelijke relatie + 5 jaar' },
          { data: 'Transactieregisters', period: '10 jaar (wettelijke vereiste)' },
          { data: 'Marketing toestemmingsregisters', period: 'Tot intrekking toestemming + 3 jaar' },
          { data: 'Website-analyse', period: '26 maanden' }
        ]
      },
      sharing: {
        title: 'Delen van Gegevens',
        text: 'Wij kunnen uw persoonsgegevens delen met:',
        recipients: [
          'Dienstverleners (IT-diensten, logistiek, betalingsverwerking)',
          'Professionele adviseurs (juridisch, boekhoudkundig)',
          'Toezichthoudende autoriteiten wanneer wettelijk vereist',
          'Andere TRH Steel-entiteiten voor interne administratieve doeleinden'
        ],
        safeguards: 'Bij overdracht van gegevens buiten de Europese Economische Ruimte zorgen wij ervoor dat passende waarborgen aanwezig zijn, waaronder door de Europese Commissie goedgekeurde Standaardcontractbepalingen.'
      },
      rights: {
        title: 'Uw Rechten onder de AVG',
        items: [
          { right: 'Inzage', desc: 'Een kopie van uw persoonsgegevens opvragen' },
          { right: 'Rectificatie', desc: 'Correctie van onjuiste gegevens verzoeken' },
          { right: 'Wissing', desc: 'Verwijdering van uw gegevens verzoeken ("recht op vergetelheid")' },
          { right: 'Beperking', desc: 'Beperking van verwerking verzoeken' },
          { right: 'Overdraagbaarheid', desc: 'Uw gegevens ontvangen in een gestructureerd, machineleesbaar formaat' },
          { right: 'Bezwaar', desc: 'Bezwaar maken tegen verwerking op basis van gerechtvaardigde belangen of direct marketing' },
          { right: 'Intrekking Toestemming', desc: 'Toestemming op elk moment intrekken zonder de eerdere verwerking te beïnvloeden' }
        ]
      },
      contact: {
        title: 'Contact voor Gegevensbescherming',
        text: 'Om uw rechten uit te oefenen of voor vragen over dit Privacybeleid, neem contact met ons op:',
        email: 'privacy@trhsteel.com',
        address: 'Functionaris Gegevensbescherming, TRH Steel, info@trhsteel.com'
      },
      complaints: {
        title: 'Recht om een Klacht in te Dienen',
        text: 'U heeft het recht om een klacht in te dienen bij een toezichthoudende autoriteit als u van mening bent dat uw gegevensbeschermingsrechten zijn geschonden. In Spanje kunt u contact opnemen met de Agencia Española de Protección de Datos (AEPD). In België kunt u contact opnemen met de Gegevensbeschermingsautoriteit (GBA).'
      }
    }
  };

  const c = content[language] || content.en;

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="privacy-policy-page">
      {/* Hero */}
      <section className="py-16 md:py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-4">
            <Shield className="h-8 w-8 text-green-500" />
            <span className="text-green-500 text-sm uppercase tracking-wider font-bold">GDPR</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight mb-4">
            {c.title}
          </h1>
          <p className="text-slate-400">{c.subtitle}</p>
          <p className="text-slate-500 text-sm mt-4">{c.lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-slate-200 p-8 md:p-12 space-y-10">
            
            {/* Introduction */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4">
                {c.intro.title}
              </h2>
              <p className="text-slate-600 leading-relaxed">{c.intro.text}</p>
            </div>

            {/* Data Controllers */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <Users className="h-5 w-5 mr-2 text-green-600" />
                {c.controllers.title}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">{c.controllers.text}</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 border border-slate-200">
                  <FlagImg code="ES" size={24} />
                  <p className="font-bold text-slate-900 mt-2">{c.controllers.spain.name}</p>
                  <p className="text-sm text-slate-600">{c.controllers.spain.id}</p>
                  <p className="text-xs text-slate-500">{c.controllers.spain.address}</p>
                </div>
                <div className="bg-slate-50 p-4 border border-slate-200">
                  <FlagImg code="BE" size={24} />
                  <p className="font-bold text-slate-900 mt-2">{c.controllers.belgium.name}</p>
                  <p className="text-sm text-slate-600">{c.controllers.belgium.id}</p>
                  <p className="text-xs text-slate-500">{c.controllers.belgium.address}</p>
                </div>
              </div>
            </div>

            {/* Data Collected */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <Database className="h-5 w-5 mr-2 text-green-600" />
                {c.dataCollected.title}
              </h2>
              <div className="space-y-2">
                {c.dataCollected.categories.map((cat, i) => (
                  <div key={i} className="flex border-b border-slate-100 pb-2">
                    <span className="font-medium text-slate-900 w-40 flex-shrink-0">{cat.type}</span>
                    <span className="text-slate-600 text-sm">{cat.examples}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Purposes */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4">
                {c.purposes.title}
              </h2>
              <div className="space-y-3">
                {c.purposes.items.map((item, i) => (
                  <div key={i} className="bg-slate-50 p-4 border-l-4 border-green-600">
                    <p className="font-bold text-slate-900">{item.purpose}</p>
                    <p className="text-slate-600 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Legal Basis */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4">
                {c.legalBasis.title}
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {c.legalBasis.items.map((item, i) => (
                  <div key={i} className="bg-slate-50 p-4 border border-slate-200">
                    <p className="font-bold text-slate-900">{item.basis}</p>
                    <p className="text-slate-600 text-sm mt-1">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Retention */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <Clock className="h-5 w-5 mr-2 text-green-600" />
                {c.retention.title}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">{c.retention.text}</p>
              <div className="bg-slate-50 border border-slate-200">
                {c.retention.periods.map((item, i) => (
                  <div key={i} className={`flex justify-between p-3 ${i < c.retention.periods.length - 1 ? 'border-b border-slate-200' : ''}`}>
                    <span className="text-slate-700">{item.data}</span>
                    <span className="font-mono text-sm text-slate-500">{item.period}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sharing */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <Share2 className="h-5 w-5 mr-2 text-green-600" />
                {c.sharing.title}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">{c.sharing.text}</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600 mb-4">
                {c.sharing.recipients.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
              <p className="text-slate-600 text-sm bg-slate-50 p-4 border border-slate-200">{c.sharing.safeguards}</p>
            </div>

            {/* Rights */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <UserCheck className="h-5 w-5 mr-2 text-green-600" />
                {c.rights.title}
              </h2>
              <div className="grid md:grid-cols-2 gap-3">
                {c.rights.items.map((item, i) => (
                  <div key={i} className="flex items-start space-x-3 p-3 bg-slate-50 border border-slate-200">
                    <div className="w-2 h-2 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="font-bold text-slate-900">{item.right}</p>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-4 flex items-center">
                <Mail className="h-5 w-5 mr-2 text-green-600" />
                {c.contact.title}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">{c.contact.text}</p>
              <div className="bg-slate-900 text-white p-4">
                <a href={`mailto:${c.contact.email}`} className="text-green-400 hover:text-green-300">{c.contact.email}</a>
              </div>
            </div>

            {/* Complaints */}
            <div className="bg-slate-50 p-6 border border-slate-200">
              <h2 className="font-heading text-lg font-bold text-slate-900 uppercase tracking-tight mb-2">
                {c.complaints.title}
              </h2>
              <p className="text-slate-600 text-sm">{c.complaints.text}</p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};
